import { gql } from '@apollo/client';

import {
  BoolEmployeePref,
  DurationEmployeePref,
  DurationInput,
  EmployeeJsonPref,
  JsonEmployeePref,
  PrefSet,
  StringEmployeePref,
  useUpdateEmployeePrefMutation,
} from 'src/generated/mloop-graphql';

import { EmployeePrefName } from 'src/utils/api/employee/constants';
import { removeTypename } from 'src/utils/removeTypename';

import { AllEmployeePref } from './types';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const UpdateEmployeePref = gql`
  mutation UpdateEmployeePref($input: EmployeePrefUpdateInput!) {
    employeePrefUpdate(input: $input) {
      employeePref {
        id
        orgId
        employeeId
        prefName
        timestamp
      }
    }
  }
`;

function useSetEmployeePref(employeeId: string, onCompleted?: () => void) {
  const [update] = useUpdateEmployeePrefMutation();
  const updateEmployeePrefs = (prefName: EmployeePrefName | AllEmployeePref, prefValue: PrefSet) => {
    return update({
      variables: {
        input: {
          employeeId,
          prefName,
          set: prefValue,
        },
      },
      awaitRefetchQueries: true,
      refetchQueries: ['EmployeePrefs'],
    }).then(() => {
      // eslint-disable-next-line promise/always-return
      if (onCompleted) {
        onCompleted();
      }
    });
  };

  return { updateEmployeePrefs };
}

export function useSetEmployeePrefBool(prefName: BoolEmployeePref, employeeId: string, onCompleted?: () => void) {
  const { updateEmployeePrefs } = useSetEmployeePref(employeeId, onCompleted);
  return (value: boolean) => {
    return updateEmployeePrefs(prefName, { boolValue: value });
  };
}

export function useSetEmployeePrefString(
  prefName: EmployeePrefName | StringEmployeePref,
  employeeId: string,
  onCompleted?: () => void
) {
  const { updateEmployeePrefs } = useSetEmployeePref(employeeId, onCompleted);
  return (value: string) => {
    return updateEmployeePrefs(prefName, { stringValue: value });
  };
}

export function useSetEmployeePrefStringArray(
  prefName: EmployeePrefName,
  employeeId: string,
  onCompleted?: () => void
) {
  const { updateEmployeePrefs } = useSetEmployeePref(employeeId, onCompleted);
  return (value: string[] | null) => {
    return updateEmployeePrefs(prefName, { stringArray: value || [] });
  };
}

export function useSetEmployeePrefJson(prefName: EmployeePrefName, employeeId: string, onCompleted?: () => void) {
  const { updateEmployeePrefs } = useSetEmployeePref(employeeId, onCompleted);
  return (value: unknown) => {
    return updateEmployeePrefs(prefName, { json: JSON.stringify(value) });
  };
}

export function useSetEmployeePrefJsonTyped<T extends EmployeeJsonPref>(
  prefName: JsonEmployeePref,
  employeeId: string,
  onCompleted?: () => void
) {
  const { updateEmployeePrefs } = useSetEmployeePref(employeeId, onCompleted);
  return (value: T) => {
    return updateEmployeePrefs(prefName, { json: JSON.stringify(removeTypename(value)) });
  };
}

/**
 * @param prefName
 * @returns mutationHook
 */
export function useSetEmployeePrefJsonArray(prefName: EmployeePrefName, employeeId: string, onCompleted?: () => void) {
  const { updateEmployeePrefs } = useSetEmployeePref(employeeId, onCompleted);
  return (jsonArray: unknown[] | null) => {
    return updateEmployeePrefs(prefName, { jsonArray: jsonArray ? jsonArray.map((json) => JSON.stringify(json)) : [] });
  };
}

export function useSetEmployeePrefTimestamp(prefName: EmployeePrefName, employeeId: string, onCompleted?: () => void) {
  const { updateEmployeePrefs } = useSetEmployeePref(employeeId, onCompleted);
  return (value: string) => {
    return updateEmployeePrefs(prefName, { timestamp: value });
  };
}

export function useSetEmployeePrefDuration(
  prefName: DurationEmployeePref,
  employeeId: string,
  onCompleted?: () => void
) {
  const { updateEmployeePrefs } = useSetEmployeePref(employeeId, onCompleted);
  return (value: DurationInput) => {
    return updateEmployeePrefs(prefName, { duration: { seconds: value.seconds } });
  };
}
